import React, { useState, useMemo, useCallback } from 'react';
import { Button, InfoHintButton } from '../common';
import PlayerSearchInput from '../common/PlayerSearchInput';
import MobileSearchField from '../common/MobileSearchField';
import { Heading, Text, Typography } from '../common/Typography';
import { Card } from '../common/Card';
import AddGuestModal from '../guests/AddGuestModal';
import DropdownMenu from '../common/DropdownMenu';
import { getGenderTextColor } from '../../utils/genderColors';
import { formatPlayerName } from '../../utils/nameUtils';

/**
 * Tournament teams: roster list + mode-first add flow (no partner yet, or full team).
 */
const AddTeamSection = ({
  players = [],
  teams = [],
  isSingles = false,
  soloEntrants = [],
  canManageTeams = false,
  hasGroupsConfigured = false,
  onAddTeam,
  onAddSinglesEntry,
  onRemoveTeam,
  onWithdrawTeam,
  onUndoWithdrawTeam,
  onRegisterSolo,
  onRemoveSolo,
  onPairSolos,
  onGuestAdded,
  onGuestUpdated,
  clubId,
  currentUser,
  seasonId,
  tournamentStatus = 'draft'
}) => {
  const [addMode, setAddMode] = useState('team'); // 'solo' | 'team' — doubles only; default team
  const [player1, setPlayer1] = useState(null);
  const [player2, setPlayer2] = useState(null);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [guestNamePrefill, setGuestNamePrefill] = useState({ firstName: '', lastName: '' });
  const [editingGuest, setEditingGuest] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rosterSearch, setRosterSearch] = useState('');

  const playersInTeams = useMemo(() => {
    const ids = new Set();
    teams.forEach((team) => {
      if (team.player1?.id) ids.add(team.player1.id);
      if (team.player2?.id) ids.add(team.player2.id);
    });
    return ids;
  }, [teams]);

  const playersInSolo = useMemo(() => {
    const ids = new Set();
    soloEntrants.forEach((row) => {
      if (row.player_id) ids.add(row.player_id);
    });
    return ids;
  }, [soloEntrants]);

  const availableCount = useMemo(() => {
    return players.filter((p) => !playersInTeams.has(p.id) && !playersInSolo.has(p.id)).length;
  }, [players, playersInTeams, playersInSolo]);

  const visibleTeams = useMemo(() => {
    const query = rosterSearch.trim().toLowerCase();
    if (!query) return teams;
    return teams.filter((team) => {
      const names = [team.player1, team.player2]
        .filter(Boolean)
        .map((player) => formatPlayerName(player))
        .join(' ')
        .toLowerCase();
      return names.includes(query);
    });
  }, [teams, rosterSearch]);

  /** Full club roster per slot (minus the other slot’s pick). Unavailable rows are labelled in the dropdown. */
  const searchPoolSlot1 = useMemo(() => {
    return players
      .filter((p) => p.id !== player2?.id)
      .sort((a, b) =>
        formatPlayerName(a).toLowerCase().localeCompare(formatPlayerName(b).toLowerCase())
      );
  }, [players, player2?.id]);

  const searchPoolSlot2 = useMemo(() => {
    return players
      .filter((p) => p.id !== player1?.id)
      .sort((a, b) =>
        formatPlayerName(a).toLowerCase().localeCompare(formatPlayerName(b).toLowerCase())
      );
  }, [players, player1?.id]);

  const getPlayerDisabledReason = useCallback(
    (p) => (playersInTeams.has(p.id) ? 'Already on a team' : null),
    [playersInTeams]
  );

  const getPlayerMutedReason = useCallback(
    (p) => (playersInSolo.has(p.id) ? 'Needs partner' : null),
    [playersInSolo]
  );

  const clearSlots = useCallback(() => {
    setPlayer1(null);
    setPlayer2(null);
    setActionError(null);
  }, []);

  const handleSetAddMode = useCallback(
    (mode) => {
      setAddMode(mode);
      setPlayer1(null);
      setPlayer2(null);
      setActionError(null);
    },
    []
  );

  const openAddGuestModal = useCallback((prefill = { firstName: '', lastName: '' }) => {
    setGuestNamePrefill({
      firstName: prefill.firstName || '',
      lastName: prefill.lastName || '',
    });
    setShowGuestModal(true);
  }, []);

  const closeAddGuestModal = useCallback(() => {
    setShowGuestModal(false);
    setGuestNamePrefill({ firstName: '', lastName: '' });
  }, []);

  const effectiveAddMode = isSingles ? 'solo' : addMode;

  const bothSelected = Boolean(player1 && player2 && player1.id !== player2.id);
  const p1Solo = player1 && playersInSolo.has(player1.id);
  const p2Solo = player2 && playersInSolo.has(player2.id);

  const showAddSolo =
    !isSingles &&
    effectiveAddMode === 'solo' &&
    canManageTeams &&
    Boolean(player1) &&
    onRegisterSolo &&
    !p1Solo;

  const showAddSinglesPlayer =
    isSingles && canManageTeams && Boolean(player1) && onAddSinglesEntry && !p1Solo;

  const showAddTeam =
    !isSingles &&
    effectiveAddMode === 'team' &&
    canManageTeams &&
    bothSelected &&
    !p1Solo &&
    !p2Solo &&
    onAddTeam;

  const showPairSolos =
    !isSingles &&
    effectiveAddMode === 'team' &&
    canManageTeams &&
    bothSelected &&
    p1Solo &&
    p2Solo &&
    onPairSolos;

  const showBlockedMix =
    !isSingles &&
    effectiveAddMode === 'team' &&
    canManageTeams &&
    bothSelected &&
    ((p1Solo && !p2Solo) || (!p1Solo && p2Solo));

  const handleAddSolo = async () => {
    if (!player1 || !onRegisterSolo) return;
    setActionError(null);
    setBusy(true);
    try {
      await onRegisterSolo(player1.id);
      clearSlots();
    } catch (e) {
      setActionError(e.message || 'Could not add player');
    } finally {
      setBusy(false);
    }
  };

  const handleAddTeam = async () => {
    if (!player1 || !player2 || !onAddTeam) return;
    setActionError(null);
    setBusy(true);
    try {
      await onAddTeam(player1.id, player2.id);
      clearSlots();
    } catch (e) {
      setActionError(e.message || 'Could not add team');
    } finally {
      setBusy(false);
    }
  };

  const handleAddSinglesPlayer = async () => {
    if (!player1 || !onAddSinglesEntry) return;
    setActionError(null);
    setBusy(true);
    try {
      await onAddSinglesEntry(player1.id);
      clearSlots();
    } catch (e) {
      setActionError(e.message || 'Could not add player');
    } finally {
      setBusy(false);
    }
  };

  const handlePairSolos = async () => {
    if (!player1 || !player2 || !onPairSolos) return;
    setActionError(null);
    setBusy(true);
    try {
      await onPairSolos(player1.id, player2.id);
      clearSlots();
    } catch (e) {
      setActionError(e.message || 'Could not pair solos');
    } finally {
      setBusy(false);
    }
  };

  const helpText = isSingles
    ? 'Search the club roster and pick a player, or add someone new as a guest from the search results. Use ⋮ on a row to withdraw (after groups) or remove.'
    : 'Search the club roster to add a pair, or switch to Add one player if they still need a partner. Use Add guest in search if they are not on the roster.';

  const postDraftNote =
    tournamentStatus !== 'draft'
      ? ' After the tournament has started, new entries may need a manual group assignment.'
      : '';

  const showRoster = tournamentStatus !== 'completed' && tournamentStatus !== 'cancelled';

  return (
    <div className="space-y-5">
      {showRoster && canManageTeams && (
        <Card variant="default" size="small" className="!p-0 overflow-visible">
          <div className="flex items-start justify-between gap-3 border-b border-border-medium px-4 py-3">
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <Heading level={3} className="mb-0 truncate text-base sm:text-lg">
                  Add players
                </Heading>
                <InfoHintButton
                  text={`${helpText}${postDraftNote}`}
                  ariaLabel="How to add players"
                  maxWidth={320}
                />
              </div>
              <Text className="mt-0.5 text-xs text-text-secondary">
                Search the club roster to add them to this tournament.
              </Text>
            </div>
            <span className="inline-flex shrink-0 items-center rounded-full bg-surface-subtle px-2.5 py-1 text-xs font-medium text-text-secondary">
              {availableCount} available to add
            </span>
          </div>

          <div className="space-y-3 bg-surface-subtle/70 px-4 py-4">
              {actionError && (
                <Card variant="warning" size="small" className="!rounded-lg !p-3">
                  <Typography variant="body" className="text-sm">
                    {actionError}
                  </Typography>
                </Card>
              )}

              {!isSingles && (
                <div
                  className="grid w-full grid-cols-2 overflow-hidden rounded-lg border border-border-medium bg-surface"
                  role="group"
                  aria-label="How to add players"
                >
                  <button
                    type="button"
                    onClick={() => handleSetAddMode('team')}
                    className={`min-h-[44px] px-3 py-2 text-sm font-medium transition-colors ${
                      addMode === 'team'
                        ? 'bg-primary text-text-inverse shadow-sm'
                        : 'text-text-secondary hover:bg-surface hover:text-text-primary'
                    }`}
                  >
                    Add a team
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSetAddMode('solo')}
                    className={`min-h-[44px] border-l border-border-medium px-3 py-2 text-sm font-medium transition-colors ${
                      addMode === 'solo'
                        ? 'bg-primary text-text-inverse shadow-sm'
                        : 'text-text-secondary hover:bg-surface hover:text-text-primary'
                    }`}
                  >
                    Add one player
                  </button>
                </div>
              )}

              {(isSingles || addMode === 'solo') && (
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-text-secondary">
                    Search to add
                  </label>
                  <PlayerSearchInput
                    placeholder="Search club roster to add…"
                    availablePlayers={searchPoolSlot1}
                    selectedPlayer={player1}
                    onSelectPlayer={(p) => {
                      setPlayer1(p);
                      setActionError(null);
                    }}
                    onRemovePlayer={() => {
                      setPlayer1(null);
                      setActionError(null);
                    }}
                    getPlayerDisabledReason={getPlayerDisabledReason}
                    getPlayerMutedReason={getPlayerMutedReason}
                    onAddNewGuest={openAddGuestModal}
                    dropdownMaxHeightClass="max-h-72"
                    className="w-full"
                  />
                </div>
              )}

              {!isSingles && addMode === 'team' && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-text-secondary">
                      First player
                    </label>
                    <PlayerSearchInput
                      placeholder="Search club roster to add…"
                      availablePlayers={searchPoolSlot1}
                      selectedPlayer={player1}
                      onSelectPlayer={(p) => {
                        setPlayer1(p);
                        setActionError(null);
                        setPlayer2((p2) => (p2?.id === p.id ? null : p2));
                      }}
                      onRemovePlayer={() => {
                        setPlayer1(null);
                        setActionError(null);
                      }}
                      getPlayerDisabledReason={getPlayerDisabledReason}
                      getPlayerMutedReason={getPlayerMutedReason}
                      onAddNewGuest={openAddGuestModal}
                      dropdownMaxHeightClass="max-h-72"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-text-secondary">
                      Partner
                    </label>
                    <PlayerSearchInput
                      placeholder="Search club roster to add…"
                      availablePlayers={searchPoolSlot2}
                      selectedPlayer={player2}
                      onSelectPlayer={(p) => {
                        setPlayer2(p);
                        setActionError(null);
                      }}
                      onRemovePlayer={() => {
                        setPlayer2(null);
                        setActionError(null);
                      }}
                      getPlayerDisabledReason={getPlayerDisabledReason}
                      getPlayerMutedReason={getPlayerMutedReason}
                      onAddNewGuest={openAddGuestModal}
                      dropdownMaxHeightClass="max-h-72"
                      className="w-full"
                    />
                  </div>
                </div>
              )}

              {(isSingles || addMode) && (
                <div className="flex flex-wrap items-center gap-2 border-t border-border-subtle pt-3">
                  {showAddSinglesPlayer && (
                    <Button variant="success" size="small" type="button" onClick={handleAddSinglesPlayer} disabled={busy}>
                      Add player
                    </Button>
                  )}
                  {showAddSolo && (
                    <Button variant="primary" size="small" type="button" onClick={handleAddSolo} disabled={busy}>
                      Add player
                    </Button>
                  )}
                  {showAddTeam && (
                    <Button variant="success" size="small" type="button" onClick={handleAddTeam} disabled={busy}>
                      Add team
                    </Button>
                  )}
                  {showPairSolos && (
                    <Button variant="success" size="small" type="button" onClick={handlePairSolos} disabled={busy}>
                      Pair into team
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="small"
                    type="button"
                    onClick={clearSlots}
                    disabled={!player1 && !player2}
                    className="ml-auto text-text-muted hover:text-text-primary"
                  >
                    Clear
                  </Button>
                </div>
              )}

              {showBlockedMix && (
                <Text className="text-sm text-warning-dark">
                  Both players must already need a partner, or both be available — not a mix.
                </Text>
              )}
              {addMode === 'solo' && player1 && p1Solo && (
                <Text className="text-sm text-text-muted">Already waiting for a partner.</Text>
              )}

            {soloEntrants.length > 0 && onRemoveSolo && (
              <div className="rounded-lg bg-warning-light/40 px-3 py-2.5">
                <Typography variant="strong" className="mb-2 block text-sm text-text-secondary">
                  Needs partner · {soloEntrants.length}
                </Typography>
                <ul className="flex flex-wrap gap-2">
                  {soloEntrants.map((row) => (
                    <li key={row.id || row.player_id} className="list-none">
                      <span className="inline-flex items-center gap-1 rounded-full border border-border-medium bg-surface-subtle px-2.5 py-1 text-sm">
                        <span className={getGenderTextColor(row.player?.gender || '')}>
                          {row.player ? formatPlayerName(row.player) : '—'}
                        </span>
                        <button
                          type="button"
                          className="px-0.5 text-lg leading-none text-danger-dark hover:text-danger"
                          title="Remove — still needs partner"
                          onClick={() => onRemoveSolo(row.player_id)}
                        >
                          ×
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>
      )}

      <Card variant="default" size="small" className="!p-0 overflow-visible">
        <div className="flex flex-col gap-3 border-b border-border-medium px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <Heading level={3} className="mb-0 truncate text-base sm:text-lg">
            {isSingles ? `Registered players (${teams.length})` : `Registered teams (${teams.length})`}
          </Heading>
          {!isSingles && soloEntrants.length > 0 ? (
            <span className="inline-flex w-fit items-center rounded-full bg-warning-light/50 px-2.5 py-1 text-xs font-medium text-warning-dark">
              {soloEntrants.length} unpaired
            </span>
          ) : null}
        </div>

        <div className="px-2 py-2 sm:px-3">
          {showRoster && !canManageTeams && soloEntrants.length > 0 && (
            <Card variant="muted" size="small" className="mb-4">
              <Typography variant="strong" className="mb-2 block text-sm">
                Unpaired players ({soloEntrants.length})
              </Typography>
              <ul className="ml-5 list-disc space-y-1 text-sm">
                {soloEntrants.map((row) => (
                  <li key={row.id || row.player_id} className={getGenderTextColor(row.player?.gender || '')}>
                    {row.player ? formatPlayerName(row.player) : 'Player'}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {teams.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border-medium bg-surface-subtle/30 px-4 py-8 text-center">
              <Text className="text-sm font-medium text-text-secondary">
                {isSingles ? 'No players registered yet' : 'No teams registered yet'}
              </Text>
              {canManageTeams && showRoster && (
                <Text className="mt-1 text-xs text-text-muted">
                  {isSingles
                    ? 'Search the roster above to add your first player.'
                    : 'Pick two players above and tap Add team.'}
                </Text>
              )}
            </div>
          ) : (
            <>
              <div className="mb-2 px-1.5">
                <MobileSearchField
                  showIcon
                  value={rosterSearch}
                  onChange={(e) => setRosterSearch(e.target.value)}
                  placeholder={isSingles ? 'Search players' : 'Search teams'}
                  aria-label={isSingles ? 'Search registered players' : 'Search registered teams'}
                  className="form-input w-full !py-2"
                />
              </div>
              {visibleTeams.length === 0 ? (
                <div className="px-4 py-6 text-center">
                  <Text className="text-sm text-text-muted">
                    {isSingles ? 'No players match that search' : 'No teams match that search'}
                  </Text>
                </div>
              ) : (
                <div className="divide-y divide-border-medium">
                  {visibleTeams.map((team) => {
                    const withdrawn = Boolean(team.withdrawn_at);
                    
                    return (
                      <div
                        key={team.id}
                        className={`group/row flex items-center justify-between gap-3 px-3.5 sm:px-4 py-2.5 text-text-primary transition-colors hover:bg-primary/[0.05] dark:hover:bg-white/[0.04] ${
                          withdrawn ? 'opacity-70' : ''
                        }`}
                      >
                        {/* Player name(s); withdrawn chip only */}
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          <Typography
                            as="div"
                            variant="body"
                            className="truncate text-sm font-medium text-text-primary"
                          >
                            {team.player2 ? (
                              <>
                                <span className={getGenderTextColor(team.player1?.gender || '')}>
                                  {formatPlayerName(team.player1)}
                                </span>
                                <span className="text-text-muted mx-1">&</span>
                                <span className={getGenderTextColor(team.player2?.gender || '')}>
                                  {formatPlayerName(team.player2)}
                                </span>
                              </>
                            ) : (
                              <span className={getGenderTextColor(team.player1?.gender || '')}>
                                {formatPlayerName(team.player1)}
                              </span>
                            )}
                          </Typography>

                          {withdrawn && (
                            <span 
                              className="inline-flex min-w-[1.25rem] flex-shrink-0 items-center justify-center rounded-md border border-border/30 bg-surface-subtle/40 px-1 py-0.5 text-[10px] font-medium text-text-muted dark:border-white/[0.08]"
                              title="Withdrawn from tournament"
                            >
                              W
                            </span>
                          )}
                        </div>

                        {/* Actions Menu */}
                        {canManageTeams && tournamentStatus !== 'completed' && tournamentStatus !== 'cancelled' && (
                          <DropdownMenu
                            align="right"
                            trigger={
                              <button
                                type="button"
                                className="flex h-11 w-11 items-center justify-center rounded-md text-text-muted hover:bg-surface-subtle hover:text-text-primary transition-colors flex-shrink-0 sm:h-8 sm:w-8"
                                aria-label="Team actions"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                </svg>
                              </button>
                            }
                            items={[
                              ...(withdrawn && onUndoWithdrawTeam
                                ? [
                                    {
                                      label: 'Undo Withdraw',
                                      icon: '↩️',
                                      onClick: () => onUndoWithdrawTeam(team.id),
                                      variant: 'default'
                                    }
                                  ]
                                : []),
                              ...(!withdrawn && onWithdrawTeam && hasGroupsConfigured
                                ? [
                                    {
                                      label: 'Withdraw',
                                      icon: '⚠️',
                                      onClick: () => onWithdrawTeam(team.id),
                                      variant: 'warning'
                                    }
                                  ]
                                : []),
                              ...(onRemoveTeam
                                ? [
                                    ...((!withdrawn && onWithdrawTeam && hasGroupsConfigured) || (withdrawn && onUndoWithdrawTeam)
                                      ? [{ divider: true }]
                                      : []),
                                    {
                                      label: 'Remove',
                                      icon: '🗑️',
                                      onClick: () => onRemoveTeam(team.id),
                                      variant: 'danger'
                                    }
                                  ]
                                : [])
                            ]}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      <AddGuestModal
        isOpen={showGuestModal || !!editingGuest}
        onClose={() => {
          closeAddGuestModal();
          setEditingGuest(null);
        }}
        guest={editingGuest}
        onGuestAdded={onGuestAdded}
        onGuestUpdated={() => {
          onGuestUpdated?.();
          setEditingGuest(null);
        }}
        sessionId={null}
        clubId={clubId}
        currentUser={currentUser}
        seasonId={seasonId}
        initialFirstName={guestNamePrefill.firstName}
        initialLastName={guestNamePrefill.lastName}
        stayOpenAfterAdd={false}
      />
    </div>
  );
};

export default AddTeamSection;
